<template>
	<div class="border-2 border-gray-800">
    <div class="overflow-hidden text-base bg-gray-800 text-white px-4 py-3">
      <div class="float-left">
        <template v-if="index == 0">Address</template>
        <template v-else>
          Previous address {{ index }}
        </template>
      </div>

      <div class="float-right">
        <template v-if="index == 0 && isCurrentLessThanThreeYears()">
          <a href="#"
            @click.prevent="moveToPrevious(residence.address.id)"
            class="no-underline hover:underline text-white text-sm">Move to previous address</a>
        </template>

        <template v-if="index > 0 && (!isCurrentLessThanThreeYears() || (isCurrentLessThanThreeYears() && numAddresses > 2))">
          <a href="#"
            @click.prevent="deleteAddress(residence.address.id)"
            class="no-underline hover:underline text-white text-sm">Delete</a>
        </template>
      </div>
    </div>

    <div class="p-4">
      <div class="flex flex-wrap">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 flex flex-wrap">
          <div class="w-1/3 pr-2">
            <BuildingTypeSelector
              propertyKey="building_type"
              objectType="address"
              :index="index"
              :value="residence.address.building_type"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3 pr-2">
            <InputText 
              label="Unit number"
              propertyKey="unit_number"
              objectType="address"
              :index="index"
              :value="residence.address.unit_number"
              :updateValue="updateValue" />
          </div>

          <div class="w-1/3">
            <InputText 
              label="Street number"
              propertyKey="street_number"
              objectType="address"
              :index="index"
              :value="residence.address.street_number"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3 mt-8 lg:mt-0 flex flex-wrap">
          <div class="w-3/4 pr-2">
            <InputText 
              label="Street name"
              propertyKey="street_name"
              objectType="address"
              :index="index"
              :value="residence.address.street_name"
              :updateValue="updateValue" />

            <div class="mt-1 text-right" v-if="residence.address.street_name != null && residence.address.street_name.length > 0">
              <a href="#" class="text-gray-700 hover:underline" @click.prevent="$modal.show('map-modal', { 
                address : getAddressText() 
              })">Show this address on map</a>
            </div>
          </div>

          <div class="w-1/4 pl-1">
            <StreetTypeSelector
              propertyKey="street_type"
              objectType="address"
              :index="index"
              :value="residence.address.street_type"
              :updateValue="updateValue" />
          </div>
        </div>

        <div class="w-full lg:w-1/3 mt-8 lg:mt-0">
          <InputText 
            label="Suburb"
            propertyKey="suburb"
            objectType="address"
            :index="index"
            :value="residence.address.suburb"
            :updateValue="updateValue" />          
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputPostcode 
            label="Postcode"
            propertyKey="postcode"
            objectType="address"
            :index="index"
            :value="residence.address.postcode"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3" ref="stateSelector">
          <StateSelector
            propertyKey="state_id"
            objectType="address"
            :index="index"
            :value="residence.address.state_id"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3" ref="countrySelector">
          <CountrySelector
            propertyKey="country_id"
            objectType="address"
            :index="index"
            :value="residence.address.country_id"
            :updateValue="updateValue" />
        </div>
      </div>

      <div class="flex flex-wrap label-line">
        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputDatePickerReadOnly 
            label="When did you move in?"
            propertyKey="moved_in_at"
            objectType="residence"
            :maxDate="today"
            :index="index"
            :value="residence.moved_in_at"
            :updateValue="updateValue" />
        </div>

        <div v-if="index > 0" class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <InputDatePickerReadOnly 
            label="When did you move out?"
            propertyKey="moved_out_at"
            objectType="residence"
            :index="index"
            :value="residence.moved_out_at"
            :updateValue="updateValue" />
        </div>

        <div class="w-full lg:w-1/3 pr-0 lg:pr-3">
          <div class="label-standard">
            How long there?
          </div>

          <div class="form-line">
            <div class="w-full border bg-gray-200 px-3" style="height:38px;line-height:38px;">
              {{ getLivingDuration(residence) }}
            </div>
          </div>
        </div>
      </div>

      <!-- display the residential status only if it's primary applicant and only if it's current address -->
      <template v-if="index == 0">
      	<template v-if="isPrimaryApplicant">
        	<ResidentialStatus></ResidentialStatus>
        </template>

        <MailingAddress :currentAddress="residence"></MailingAddress>
      </template>
   	</div>
  </div>
</template>

<script>
  import moment                   from 'moment'
	import InputText 					      from '@/shared/components/form/InputText'
  import InputPostcode            from '@/shared/components/form/InputPostcode'
  import StateSelector            from '@/shared/components/form/StateSelector'
  import StreetTypeSelector       from '@/shared/components/form/StreetTypeSelector'
  import BuildingTypeSelector     from '@/shared/components/form/BuildingTypeSelector'
  import CountrySelector          from '@/shared/components/form/CountrySelector'
  import InputDatePickerReadOnly  from '@/shared/components/form/InputDatePickerReadOnly'
  import ResidentialStatus        from './ResidentialStatus'
  import MailingAddress           from './MailingAddress'

	export default {
		
		props : {
			index : {
				type    : Number,
				required: true
			},

			residence : {
				type    : Object,
				required: true
			},

			updateValue : {
				type    : Function,
				required: false
			},

      moveToPrevious : {
        type    : Function,
        required: false
      },

      deleteAddress : {
        type    : Function,
        required: false
      },

      numAddresses : {
        type    : Number,
        required: false
      },

      isCurrentLessThanThreeYears : {
        type    : Function,
        required: false
      }
		},

    components: {
      InputText,
      InputDatePickerReadOnly,
      StateSelector,
      CountrySelector,
      InputPostcode,
      MailingAddress,
      ResidentialStatus,
      StreetTypeSelector,
      BuildingTypeSelector
    },

    methods : {
      getLivingDuration(address) {
        let text = ""

        if(address.moved_in_at != null && address.moved_in_at.length > 0) {

          let now = moment()
          
          if(address.moved_out_at != null) {
            now = moment(address.moved_out_at, "YYYY-MM-DD")
          }
          
          let then   = moment(address.moved_in_at, "YYYY-MM-DD")
          let months = now.diff(then, 'months')

          let years = Math.floor(months / 12)
          text += years + (years == 1 ? " year " : " years ")

          if(months % 12 > 0) {
            text += (text.length > 0 ? " and " : "") + (months % 12 ) + (months % 12 == 1 ? ' month' : ' months')
          } else {
            text += " and 0 months"
          }
        }

        return text
      },

      getAddressText() {
        let address = []

        if(this.residence.address.unit_number != null) {
          address.push(this.residence.address.unit_number)
          address.push("/")
        }

        if(this.residence.address.street_number != null) {
          address.push(this.residence.address.street_number)
        }

        if(this.residence.address.street_name != null) {
          address.push(this.residence.address.street_name)
        }

        if(this.residence.address.street_type != null) {
          address.push(this.residence.address.street_type)
        }

        if(this.residence.address.suburb != null) {
          address.push(this.residence.address.suburb)
        }

        if(this.residence.address.postcode != null) {
          address.push(this.residence.address.postcode)
        }

        if(this.residence.address.state != null) {
          address.push(this.residence.address.state)
        }

        if(this.residence.address.country != null) {
          address.push(this.residence.address.country)
        }

        return address.join(' ')
      }
    }
	}
</script>